import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserPlus, Copy, Check, Trash2, Loader2, Users, Link2, MailCheck, Clock } from "lucide-react";

export default function Invites() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copiedTok, setCopiedTok] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/invites");
      setInvites(data);
    } catch { toast.error("Failed to load invites"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/invites");
      toast.success("Invite link created");
      load();
    } catch { toast.error("Could not create invite"); }
    finally { setCreating(false); }
  };

  const revoke = async (token) => {
    try { await api.delete(`/invites/${token}`); load(); toast.success("Invite removed"); }
    catch { toast.error("Delete failed"); }
  };

  const linkFor = (token) => `${window.location.origin}/register/${token}`;

  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(linkFor(token));
      setCopiedTok(token); setTimeout(() => setCopiedTok(""), 1800);
      toast.success("Invite link copied");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="p-8 lg:p-10 max-w-4xl">
      <div className="border-b border-white/10 pb-6 mb-8 flex items-end justify-between">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#484F58] mb-2 font-mono-jb">Admin</div>
          <h1 className="font-mono-jb text-3xl sm:text-4xl font-extrabold tracking-tighter text-white flex items-center gap-3">
            <Users className="text-[#00F0FF]" size={32} /> Team Invites
          </h1>
          <p className="text-[#8B949E] mt-3 leading-relaxed">Generate a single-use link to add a team member. They create a <span className="text-white">user</span> account (view, generate & download docs) — they cannot invite others or change AI settings.</p>
        </div>
        <Button data-testid="create-invite-button" onClick={create} disabled={creating}
          className="rounded-sm bg-[#00F0FF] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00F0FF] hover:glow-cyan h-11 px-5 shrink-0">
          {creating ? <Loader2 className="animate-spin" size={16} /> : <><UserPlus size={16} className="mr-1" /> New Invite</>}
        </Button>
      </div>

      {loading ? (
        <div className="font-mono-jb text-[#8B949E] text-sm">Loading...</div>
      ) : invites.length === 0 ? (
        <div className="border border-dashed border-white/10 bg-[#0B0C10] p-12 text-center" data-testid="empty-invites">
          <Link2 className="mx-auto text-[#484F58] mb-3" size={36} />
          <div className="font-mono-jb text-white mb-1">No invites yet</div>
          <p className="text-[#8B949E] text-sm">Create an invite link and send it to your colleague.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="invites-list">
          {invites.map((inv) => (
            <div key={inv.token} data-testid={`invite-row-${inv.token}`}
              className="border border-white/10 bg-[#0B0C10] p-4 hover:border-white/25 transition-colors duration-200">
              <div className="flex items-center gap-3">
                {inv.used ? (
                  <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono-jb text-[#00FF41] border border-[#00FF41]/30 px-2 py-1"><MailCheck size={12} /> Used</span>
                ) : (
                  <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono-jb text-[#EAE04A] border border-[#EAE04A]/30 px-2 py-1"><Clock size={12} /> Pending</span>
                )}
                <div className="min-w-0 flex-1">
                  {inv.used ? (
                    <div className="font-mono-jb text-sm text-white truncate">{inv.used_by}</div>
                  ) : (
                    <div className="font-mono-jb text-xs text-[#8B949E] truncate">{linkFor(inv.token)}</div>
                  )}
                  <div className="text-[10px] text-[#484F58] mt-0.5">Created {new Date(inv.created_at).toLocaleString("en-AU", { timeZone: "Australia/Sydney", year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short" })}</div>
                </div>
                {!inv.used && (
                  <Button data-testid={`copy-invite-${inv.token}`} onClick={() => copy(inv.token)} variant="outline"
                    className="rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-[#00F0FF] font-mono-jb text-[11px] uppercase h-9">
                    {copiedTok === inv.token ? <Check size={14} /> : <><Copy size={14} className="mr-1" /> Copy Link</>}
                  </Button>
                )}
                <button data-testid={`revoke-invite-${inv.token}`} onClick={() => revoke(inv.token)}
                  className="text-[#484F58] hover:text-[#FF3B30] transition-colors duration-200"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
